/**
 * Real-time WebSocket client (singleton).
 *
 * Connects to the FastAPI `/ws` endpoint, authenticating with the stored access
 * token via query param (browsers can't set headers on a WebSocket). Handles:
 *   - auto-reconnect with capped exponential backoff,
 *   - a heartbeat ping so idle connections aren't dropped by proxies,
 *   - a tiny pub/sub so features subscribe to server events by name.
 *
 * The socket is ADDITIVE: every feature it powers (notifications, chat) also has
 * a REST path, so a closed socket degrades gracefully rather than breaking.
 *
 * Server → client frames are `{ event, data }`. Subscribe with `socket.on(event,
 * handler)`; send with `socket.send(event, data)`. Lifecycle (connect on login,
 * disconnect on logout) is driven by AuthContext.
 */
import env from 'config/env';
import tokenManager from 'util/tokenManager';

const HEARTBEAT_MS = 25000; // keep-alive ping interval
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30000;

/** Derive the ws(s):// base URL from the configured API base (or same-origin). */
function wsBaseUrl() {
  const base = env.apiBaseUrl || '';
  if (!base) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}`;
  }
  return base.startsWith('https')
    ? base.replace(/^https/i, 'wss')
    : base.replace(/^http/i, 'ws');
}

class SocketClient {
  constructor() {
    this.ws = null;
    this.shouldReconnect = false;
    this.backoff = BACKOFF_MIN_MS;
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    // event name -> Set<handler>
    this.listeners = new Map();
  }

  get isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /** Open the connection (idempotent). Call once the user is authenticated. */
  connect() {
    this.shouldReconnect = true;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN
      || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const token = tokenManager.getToken();
    if (!token) return; // nothing to authenticate with yet

    let ws;
    try {
      ws = new WebSocket(`${wsBaseUrl()}/ws?token=${encodeURIComponent(token)}`);
    } catch {
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return; // superseded by a newer socket — ignore
      this.backoff = BACKOFF_MIN_MS;
      this._startHeartbeat();
    };

    ws.onmessage = (event) => {
      // An orphaned socket (replaced by a reconnect) must NOT emit — otherwise
      // every server event fires on both sockets and listeners run twice.
      if (this.ws !== ws) return;
      let frame;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return; // ignore non-JSON frames
      }
      if (frame && frame.event) this._emit(frame.event, frame.data);
    };

    ws.onclose = () => {
      // A stale socket's late close must not clobber the live `this.ws` nor
      // schedule a spurious reconnect.
      if (this.ws !== ws) return;
      this._stopHeartbeat();
      this.ws = null;
      if (this.shouldReconnect) this._scheduleReconnect();
    };

    ws.onerror = () => {
      if (this.ws !== ws) return;
      // onclose fires next and handles reconnect; just close to be safe.
      try { ws.close(); } catch { /* already closing */ }
    };
  }

  /** Close intentionally and stop reconnecting (call on logout). */
  disconnect() {
    this.shouldReconnect = false;
    this._stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this._discardSocket(this.ws);
      this.ws = null;
    }
  }

  /**
   * Fully retire a socket: detach every handler BEFORE closing so its async
   * `onclose` can never run `this.ws = null` / reschedule a reconnect after we
   * have already moved on, and its `onmessage` can never emit again. This is
   * what prevents duplicate live sockets (and duplicate events) across a
   * reconnect — e.g. React StrictMode firing the auth effect twice.
   */
  _discardSocket(ws) {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try { ws.close(); } catch { /* already closed */ }
  }

  /** Reconnect with the current (possibly refreshed) token. */
  reconnect() {
    this.disconnect();
    this.connect();
  }

  /** Send a client event. Returns true if it went out, false if not connected. */
  send(event, data) {
    if (!this.isConnected) return false;
    try {
      this.ws.send(JSON.stringify({ event, data }));
      return true;
    } catch {
      return false;
    }
  }

  /** Subscribe to a server event. Returns an unsubscribe function. */
  on(event, handler) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(handler);
    return () => {
      const set = this.listeners.get(event);
      if (set) {
        set.delete(handler);
        if (!set.size) this.listeners.delete(event);
      }
    };
  }

  _emit(event, data) {
    const set = this.listeners.get(event);
    if (!set) return;
    set.forEach((fn) => {
      try {
        fn(data);
      } catch {
        /* one listener's error must not break the others */
      }
    });
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) this.connect();
    }, delay);
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send('ping');
    }, HEARTBEAT_MS);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}

export const socket = new SocketClient();
export default socket;
